// Charter request details: header, the request's cards, invoices, quotes and the quote form.
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Popconfirm, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import {
  cancelCharterRequest,
  deleteCharterQuote,
  dismissCharterRequestPaymentRequired,
  dismissCharterRequestQuoteRefresh,
  getCharter,
  sendCharterQuotes,
} from '../../api/endpoints';
import { CHARTER_REQUEST_STATUS_COLORS, CHARTER_REQUEST_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { CharterInvoicesCard } from '../../components/CharterInvoicesCard';
import { CustomerCard } from './CustomerCard';
import { ItineraryCard } from './ItineraryCard';
import { AircraftDetailsCard } from './AircraftDetailsCard';
import { PricingCard } from './PricingCard';
import { RevenueCard } from './RevenueCard';
import { QuotesCard } from './QuotesCard';
import { useQuoteForm } from './useQuoteForm';
import { QuoteFormModal } from './QuoteFormModal';
import { withAbbr } from '../../data/airportTime';

const { Title, Text } = Typography;

export function ChartersDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const backTo = (loc.state as { from?: string } | null)?.from ?? '/charters';
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();

  const detail = useQuery({ queryKey: ['charter', id], queryFn: () => getCharter(id), enabled: !!id });
  const r = detail.data;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['charter', id] });

  // The quote form's state, watchers, queries, create/update and effects.
  const quote = useQuoteForm({ id, r, invalidate });
  const { openEdit } = quote;

  const deleteMut = useMutation({
    mutationFn: (qid: string) => deleteCharterQuote(qid),
    onSuccess: () => { message.success('Quote deleted'); invalidate(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });
  const sendMut = useMutation({
    mutationFn: () => sendCharterQuotes(id),
    onSuccess: () => {
      message.success('Quotes sent to the user');
      invalidate();
      // SHAR-1116 — sending clears "Quote refresh": the list's row and counter too.
      qc.invalidateQueries({ queryKey: ['charters'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Send failed'),
  });
  // SHAR-116 — cancelling releases the user's pre-authorisation, so the wording
  // on the confirmation says so: it is not an undoable tidy-up.
  // SHAR-1105 — remove the "Payment required" ad-hoc label (admin panel only).
  const dismissPaymentRequiredMut = useMutation({
    mutationFn: () => dismissCharterRequestPaymentRequired(id),
    onSuccess: () => {
      message.success('Removed “Payment required”');
      invalidate();
      // SHAR-1105 (retest) — and the requests list (its row and its counter).
      qc.invalidateQueries({ queryKey: ['charters'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not remove the label'),
  });
  // SHAR-1116 — remove the "Quote refresh" ad-hoc label; list and page in sync.
  const dismissQuoteRefreshMut = useMutation({
    mutationFn: () => dismissCharterRequestQuoteRefresh(id),
    onSuccess: () => {
      message.success('Removed “Quote refresh”');
      invalidate();
      qc.invalidateQueries({ queryKey: ['charters'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not remove the label'),
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelCharterRequest(id),
    onSuccess: () => {
      message.success(
        r?.preAuthExpired
          ? 'Charter request cancelled. The pre-authorisation had already expired; nothing is held.'
          : 'Charter request cancelled and pre-authorisation released',
      );
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Cancel failed'),
  });

  // A payload without `quotes` is not a charter request, whatever the request
  // for it returned. Found by the new e2e smoke suite: /charters/bookings is a
  // believable URL - it is the API's own path for the bookings list - and it
  // matches charters/:id, so this page fetched /admin/charters/bookings, was
  // handed the BOOKINGS LIST, and crashed on `r.quotes.some(...)` further down.
  // The admin saw a white page with nothing to explain it. Truthiness alone was
  // never enough of a test here.
  if (detail.isLoading || !r || !Array.isArray(r.quotes)) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>Back</Button>
        <div style={{ textAlign: 'center', padding: 48 }}>{detail.isLoading ? <Spin /> : <Text type="secondary">Not found.</Text>}</div>
      </Space>
    );
  }

  // PO, 29 Sep — see the Add quote button.
  const isBooked = r?.status === 'booked' || !!(r as any)?.bookingId;
  const openCreate = () => quote.openCreate(r);

  const hasDrafts = r.quotes.some((q) => q.status === 'created');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* SHAR-849 — back to the page and tab this was opened from. The list
          keeps both in its URL and hands it over on the row click; a detail
          opened from a link or a refresh has no such state and falls back to
          the bare list, which is the old behaviour and the only sensible one. */}
      <Button icon={<ArrowLeftOutlined />} onClick={() => nav(backTo)}>Back to charters</Button>

      {/* The Bookings header: the title, a secondary line naming the route and
          departure so the page identifies itself without scrolling, and the
          status on the right. This page had the title and the tag but no
          secondary line. Charter statuses keep their own colours and labels -
          they are a different set from bookings, and were left separate on
          purpose. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {/* Regression 29 Sep — the reference as the list, the booking page and
                the emails print it (CH- + last eight, upper case). */}
            Charter <Text code>{`CH-${r.id.slice(-8).toUpperCase()}`}</Text>
          </Title>
          <Text type="secondary">
            {r.originCode} → {r.destCode} · {withAbbr(`${r.departureDate} ${r.departureTime}`, r.departureZoned)}
          </Text>
        </div>
        <Space>
          {/* SHAR-117 — a booked request links straight to its booking. */}
          {r.bookingId ? (
            <Button onClick={() => nav(`/charter-bookings/${r.bookingId}`)}>
              View booking {r.bookingReference ?? r.bookingId.slice(-8)}
            </Button>
          ) : null}
          {/* SHAR-116 — cancellation is confirmed in a pop-up, and is offered
              only while there is something to cancel. */}
          {r.status !== 'cancelled' && r.status !== 'booked' ? (
            <Popconfirm
              title="Cancel this charter request?"
              description={
                <span style={{ display: 'inline-block', maxWidth: 280 }}>
                  {/* Regression 28 Sep (SHAR-1092 follow-up) — a hold that had
                      already expired is not "released" by the cancel. */}
                  {r.preAuthExpired
                    ? "The request is marked cancelled. The user's pre-authorisation had already expired; nothing is held."
                    : "The request is marked cancelled and the user's pre-authorisation is released back to them."}{' '}
                  They will stop seeing the quotes; you will still see them here.
                </span>
              }
              okText="Cancel request"
              okButtonProps={{ danger: true, loading: cancelMut.isPending }}
              cancelText="Keep"
              onConfirm={() => cancelMut.mutate()}
            >
              <Button danger>Cancel request</Button>
            </Popconfirm>
          ) : null}
          <Tag color={CHARTER_REQUEST_STATUS_COLORS[r.status]} style={{ fontSize: 14, padding: '2px 10px' }}>
            {statusLabel(CHARTER_REQUEST_STATUS_LABELS, r.status, r.statusLabel)}
          </Tag>
        </Space>
      </div>

      {/* Bookings splits its detail into themed cards - Flight details,
          Itinerary, Aircraft details, Pricing - each a bordered Descriptions of
          four to eight fields. This screen carried all twelve fields in one
          "Request" card, which became a long strip once operator, category, tail
          number and the separated phone/email were added on 17 Aug. Same fields,
          same values, same grid; grouped the way Bookings groups them. */}
      <CustomerCard r={r} />

      <ItineraryCard r={r} />

      <AircraftDetailsCard r={r} />

      <PricingCard
        r={r}
        dismissQuoteRefreshMut={dismissQuoteRefreshMut}
        dismissPaymentRequiredMut={dismissPaymentRequiredMut}
      />

      {/* SHAR-995 — revenue, as on the ShareJet booking page. No seat fields:
          a charter request books the whole aircraft, so "Seats booked" would
          have nothing to say. SHAR-1059 - always the request's own calculation,
          frozen when it was made; the booking's revenue is on the booking. */}
      {r.revenue && <RevenueCard revenue={r.revenue} />}

      {/* SHAR-811 — the Invoices block, same as the ShareJet booking page. */}
      <CharterInvoicesCard invoices={(r as any).invoices} />

      <QuotesCard
        r={r}
        isBooked={isBooked}
        hasDrafts={hasDrafts}
        openCreate={openCreate}
        openEdit={openEdit}
        sendMut={sendMut}
        deleteMut={deleteMut}
        nav={nav}
      />

      <QuoteFormModal quote={quote} />
    </Space>
  );
}
